import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import {
  Box,
  Breadcrumbs,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import {
  UpdateCompanyForm,
  updateCompanyFormSchema
} from '@shared/dto/companies.dto'
import { CompanyType } from '@shared/types/companies.types'
import { Permission } from '@shared/types/roles.types'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router'
import CompanyAvatar from '../../components/CompanyAvatar'
import UploadFileArea from '../../components/forms/UploadFileArea'
import { LoaderButton } from '../../components/LoaderButton'
import ViewHeader from '../../components/ViewHeader'
import { useAuthStore } from '../../hooks/useAuthStore'
import { useUpdateCompanyMutation } from '../../hooks/useMutations/useCompaniesMutations'
import { useCompany, useCompanyUser } from '../../hooks/useQueries/useCompanies'
import { useSnackbar } from '../../hooks/useSnackbar'
import { spanishProvinces } from '../../utils/constants.utils'

export default function CompaniesEdit() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const params = useParams()
  const companyId = params.companyId!
  const { data: company, isLoading: isCompanyLoading } = useCompany(companyId)
  const { userId } = useAuthStore()
  const { data: user, isLoading: isUserLoading } = useCompanyUser(
    companyId,
    userId!
  )
  const updateCompanyMutation = useUpdateCompanyMutation(companyId)
  const { showSnackbar } = useSnackbar()

  useEffect(() => {
    const hasUpdatePermission = user?.permissions?.includes(
      Permission.COMPANIES_UPDATE
    )

    if (user && !user.role.fullAccess && !hasUpdatePermission) {
      navigate('/access-denied')
    }
  }, [user, navigate])

  const {
    control,
    watch,
    setValue,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isValid }
  } = useForm<UpdateCompanyForm>({
    defaultValues: {
      name: '',
      tin: '',
      type: CompanyType.CONSTRUCTOR,
      address: '',
      zipcode: '',
      country: '',
      state: '',
      webUrl: '',
      logoUri: null
    },
    resolver: standardSchemaResolver(updateCompanyFormSchema),
    mode: 'onChange'
  })

  const logoUri = watch('logoUri')

  useEffect(() => {
    if (company) {
      reset({
        name: company.name,
        tin: company.tin,
        type: company.type,
        address: company.address || '',
        zipcode: company.zipcode || '',
        country: company.country || '',
        state: company.state || '',
        webUrl: company.webUrl || '',
        logoUri: company.logoUri
      })
    }
  }, [company, reset])

  const onSubmit = async (data: UpdateCompanyForm) => {
    updateCompanyMutation.mutate(data, {
      onSuccess: () => {
        showSnackbar({
          message: t('edit_company.success'),
          severity: 'success'
        })
      },
      onError: () => {
        showSnackbar({
          message: t('edit_company.error'),
          severity: 'error'
        })
      }
    })
  }

  if (isCompanyLoading || isUserLoading) {
    return (
      <Stack direction='column' spacing='1.5rem'>
        <Breadcrumbs separator='›'>
          <Skeleton width={120} height={20} />
          <Skeleton width={100} height={20} />
        </Breadcrumbs>
        <Skeleton variant='rectangular' height={40} width={300} />
        <Stack direction='row' spacing='1.5rem'>
          <Skeleton variant='rounded' width={375} height={375} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant='text' width='30%' height={40} sx={{ mt: 2 }} />
            <Stack direction='row' spacing='1.5rem' sx={{ mt: 2 }}>
              <Skeleton variant='rectangular' height={56} width='100%' />
              <Skeleton variant='rectangular' height={56} width='100%' />
            </Stack>
            <Stack direction='row' spacing='1.5rem' sx={{ mt: 2 }}>
              <Skeleton variant='rectangular' height={56} width='100%' />
              <Skeleton variant='rectangular' height={56} width='100%' />
            </Stack>
            <Stack direction='row' spacing='1.5rem' sx={{ mt: 2 }}>
              <Skeleton variant='rectangular' height={56} width='100%' />
              <Skeleton variant='rectangular' height={56} width='100%' />
            </Stack>
            <Stack direction='row' spacing='1.5rem' sx={{ mt: 2 }}>
              <Skeleton variant='rectangular' height={56} width='100%' />
              <Skeleton variant='rectangular' height={56} width='100%' />
            </Stack>
            <Box sx={{ display: 'flex', gap: '1.5rem', mt: 3 }}>
              <Skeleton variant='rectangular' width={140} height={40} />
              <Skeleton variant='rectangular' width={140} height={40} />
            </Box>
          </Box>
        </Stack>
      </Stack>
    )
  }

  return (
    <Stack direction='column' spacing='1.5rem'>
      <Breadcrumbs separator='›' sx={{ fontSize: '0.875rem' }}>
        <Link
          to={`/companies/${companyId}/workspaces`}
          style={{ textDecoration: 'none' }}
        >
          <Typography
            variant='body2'
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
              color: 'text.primary',
              transition: 'color 0.2s',
              '&:hover': {
                color: 'primary.main',
                textDecoration: 'underline'
              }
            }}
          >
            {t('edit_company.header')}
          </Typography>
        </Link>
        <Typography variant='body2' color='text.primary'>
          {t('user.my_profile')}
        </Typography>
      </Breadcrumbs>
      <ViewHeader
        header={t('edit_company.header')}
        subheader={t('edit_company.subheader')}
      />
      <Stack direction='row' spacing='1.5rem'>
        {logoUri ? (
          <CompanyAvatar
            logoUri={logoUri}
            setValue={setValue}
            name='logoUri'
            acceptedFormats='image/png, image/jpg'
            label={t('edit_company.form.logo.button')}
          />
        ) : (
          <UploadFileArea
            label={t('edit_company.form.logo.title')}
            name='logoUri'
            setValue={setValue}
            acceptedFormats='image/png, image/jpg'
            acceptedFormatsLabel={t('edit_company.form.logo.formats')}
          />
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            width: '100%'
          }}
        >
          <Typography sx={{ marginTop: '1rem' }} variant='h5'>
            {t('edit_company.form.title')}
          </Typography>
          <Stack direction='row' spacing='1.5rem'>
            <Controller
              name='name'
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  label={t('edit_company.form.name')}
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name && t(errors.name.message as string)}
                  sx={{
                    '& .MuiInputBase-root': {
                      backgroundColor: '#FFF'
                    }
                  }}
                />
              )}
            />
            <Controller
              name='tin'
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  label={t('edit_company.form.tin')}
                  fullWidth
                  error={!!errors.tin}
                  helperText={errors.tin && t(errors.tin.message as string)}
                  sx={{
                    '& .MuiInputBase-root': {
                      backgroundColor: '#FFF'
                    }
                  }}
                />
              )}
            />
          </Stack>
          <Stack direction='row' spacing='1.5rem'>
            <Controller
              name='address'
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('edit_company.form.address')}
                  fullWidth
                  error={!!errors.address}
                  helperText={errors.address?.message}
                  sx={{
                    '& .MuiInputBase-root': {
                      backgroundColor: '#FFF'
                    }
                  }}
                />
              )}
            />
            <FormControl fullWidth>
              <InputLabel>{t('edit_company.form.state')}</InputLabel>
              <Controller
                name='state'
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    label={t('edit_company.form.state')}
                    sx={{
                      background: '#FFF'
                    }}
                  >
                    {spanishProvinces.map(province => (
                      <MenuItem key={province} value={province}>
                        {province}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </Stack>
          <Stack direction='row' spacing='1.5rem'>
            <FormControl fullWidth>
              <InputLabel>{t('edit_company.form.country')}</InputLabel>
              <Controller
                name='country'
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    label={t('edit_company.form.country')}
                    sx={{ background: '#FFF' }}
                    renderValue={selected => {
                      if (selected === '') {
                        return (
                          <Typography color='text.secondary'>
                            {t('address.company.form.country')}
                          </Typography>
                        )
                      }
                      return t('common.spain')
                    }}
                  >
                    <MenuItem key={t('common.spain')} value={t('common.spain')}>
                      {t('common.spain')}
                    </MenuItem>
                  </Select>
                )}
              />
            </FormControl>
            <Controller
              name='zipcode'
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('edit_company.form.zip_code')}
                  fullWidth
                  error={!!errors.zipcode}
                  helperText={
                    errors.zipcode && t(errors.zipcode.message as string)
                  }
                  sx={{
                    '& .MuiInputBase-root': {
                      backgroundColor: '#FFF'
                    }
                  }}
                />
              )}
            />
          </Stack>
          <Stack direction='row' spacing='1.5rem'>
            <Controller
              name='webUrl'
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('edit_company.form.web_url')}
                  fullWidth
                  error={!!errors.webUrl}
                  helperText={
                    errors.webUrl && t(errors.webUrl.message as string)
                  }
                  sx={{
                    '& .MuiInputBase-root': {
                      backgroundColor: '#FFF'
                    }
                  }}
                />
              )}
            />
            <FormControl fullWidth>
              <InputLabel>{t('edit_company.form.type')}</InputLabel>
              <Controller
                name='type'
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    fullWidth
                    label={t('edit_company.form.type')}
                    disabled
                    sx={{ background: '#FFF' }}
                  >
                    <MenuItem value={CompanyType.CONSTRUCTOR}>
                      {t('common.constructor_type')}
                    </MenuItem>
                    <MenuItem value={CompanyType.PROVIDER}>
                      {t('common.provider')}
                    </MenuItem>
                  </Select>
                )}
              />
            </FormControl>
          </Stack>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: '1.5rem',
              marginTop: '1.5rem'
            }}
          >
            <LoaderButton
              label={t('common.save_changes')}
              onClick={handleSubmit(onSubmit)}
              variant='contained'
              disabled={!isDirty || !isValid || updateCompanyMutation.isPending}
              loading={updateCompanyMutation.isPending}
            />
            <Button variant='outlined'>{t('common.cancel')}</Button>
          </Box>
        </Box>
      </Stack>
    </Stack>
  )
}
